import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getDailyMood } from "./dailyMoods";
import { getThemeById } from "../recommend/themes";

export const DailyMoodContext = createContext(null);

const STORAGE_KEY = "dailyMood"; // { moodId, totonoeId, date }

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { moodId: null, totonoeId: null };
    const parsed = JSON.parse(raw);
    if (parsed.date !== todayStr()) return { moodId: null, totonoeId: null };
    return { moodId: parsed.moodId || parsed.id || null, totonoeId: parsed.totonoeId || null };
  } catch {
    return { moodId: null, totonoeId: null };
  }
}

function saveToStorage(moodId, totonoeId) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ moodId, totonoeId, date: todayStr() }));
  } catch {}
}

export function DailyMoodProvider({ children }) {
  const initial = loadFromStorage();
  const [moodId, setMoodId] = useState(initial.moodId);
  const [totonoeId, setTotonoeId] = useState(initial.totonoeId);

  const setTodayMood = useCallback((id) => {
    setMoodId(id);
    saveToStorage(id, totonoeId);
  }, [totonoeId]);

  const setTotonoe = useCallback((id) => {
    setTotonoeId(id);
    saveToStorage(moodId, id);
  }, [moodId]);

  const mood = useMemo(() => getDailyMood(moodId), [moodId]);
  const totonoe = useMemo(() => getThemeById(totonoeId), [totonoeId]);

  const needsSelection = !moodId;

  const value = useMemo(
    () => ({ mood, moodId, setTodayMood, totonoe, totonoeId, setTotonoe, needsSelection }),
    [mood, moodId, setTodayMood, totonoe, totonoeId, setTotonoe, needsSelection]
  );

  return (
    <DailyMoodContext.Provider value={value}>
      {children}
    </DailyMoodContext.Provider>
  );
}

export function useDailyMood() {
  return useContext(DailyMoodContext);
}
